import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Building2 } from '@/components/icons';
import { useProperty } from '@/contexts/property-context';
import { EmptyState } from '@/components/common/EmptyState';
import type { ModuleId } from '@/lib/modules';

interface RequireModuleProps {
  module: ModuleId;
  feature: string;
  children: React.ReactNode;
}

export const RequireModule: React.FC<RequireModuleProps> = ({ module, feature, children }) => {
  const { hasModule, selectedProperty } = useProperty();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!hasModule(module)) {
    const propertyName = selectedProperty?.name;
    return (
      <EmptyState
        icon={Building2}
        title={t('common.module_unavailable_title', "{{feature}} isn't available here", { feature })}
        description={
          propertyName
            ? t(
              'common.module_unavailable_named',
              '{{property}} doesn\'t have {{feature}} enabled. Switch to a different property from the top bar, or contact your property manager.',
              { property: propertyName, feature: feature.toLowerCase() }
            )
            : t(
              'common.module_unavailable_generic',
              'This property doesn\'t have {{feature}} enabled. Switch to a different property or contact your property manager.',
              { feature: feature.toLowerCase() }
            )
        }
        actionLabel={t('common.back_to_overview', 'Back to Overview')}
        onAction={() => navigate('/app')}
      />
    );
  }

  return <>{children}</>;
};
